

// Root router. Feature flag integration is opt-in per route via data metadata:
//
//   featureCode:    'MY_FLAG'   → featureFlagGuard blocks the route if disabled
//   featureVariant: 'MY_FLAG'   → featureFlagResolver swaps the component version
//
// Routes without either property behave exactly as before — no breaking changes.

import { Routes, RouterModule } from '@angular/router';
import { NgModule }             from '@angular/core';

import { AdminLayoutComponent } from './components/common/layouts/admin-layout/admin-layout.component';
import { AuthLayoutComponent }  from './components/common/layouts/auth-layout/auth-layout.component';

import { featureFlagGuard }     from './core/feature-flags/feature-flags.guard';
import { JobsActiveMapView } from './views/site-review/jobs-active-master/jobs-active-map-view/jobs-active-map-view';

export const rootRouterConfig: Routes = [
  {
    path:        '',
    redirectTo:  'reviews/jobs',
    pathMatch:   'full'
  },

  {
    path:      'active-jobs-map-view',
    component: JobsActiveMapView,
    
  },

  // ── Auth layout (login, 404, etc.) — no feature flags needed ──────────────
  {
    path:      '',
    component: AuthLayoutComponent,
    children: [
      {
        path:         'sessions',
        loadChildren: () => import('./views/sessions/sessions.module').then(m => m.SessionsModule),
        data:         { title: 'Session' }
      }
    ]
  },

  // ── Admin layout — feature flags applied per child route as needed ─────────
  {
    path:      '',
    component: AdminLayoutComponent,
    children: [

      // No flag — always visible
      {
        path:         'dashboard',
        loadChildren: () => import('./views/dashboard/dashboard.module').then(m => m.DashboardModule),
        data:         { title: 'Dashboard', breadcrumb: 'Dashboard' }
      },

      // No flag — always visible
      {
        path:         'customers',
        loadChildren: () => import('./views/customer/customer.module').then(m => m.CustomerModule),
        data:         { title: 'Customers', breadcrumb: 'Customers' }
      },

      // No flag — core module, always visible
      {
        path:         'reviews',
        loadChildren: () => import('./views/site-review/site-review.module').then(m => m.SiteReviewModule),
        data:         { title: 'Reviews', breadcrumb: 'Reviews' }
        // When you want to gate the whole Reviews section behind a flag:
        // canActivate: [featureFlagGuard],
        // data: { title: 'Reviews', breadcrumb: 'Reviews', featureCode: 'REVIEWS' }
      },

      // No flag — admin is role-gated by RouteGuardService instead
      {
        path:         'admin',
        loadChildren: () => import('./views/admin/admin.module').then(m => m.AdminModule),
        data:         { title: 'Administration', breadcrumb: 'Administration' }
      },

      // No flag — core module
      {
        path:         'finance',
        loadChildren: () => import('./views/finance/finance.module').then(m => m.FinanceModule),
        data:         { title: 'Finance', breadcrumb: 'Finance' }
        // Example — gate behind a flag when you create a Finance V2:
        // canActivate: [featureFlagGuard],
        // data: { title: 'Finance', breadcrumb: 'Finance', featureCode: 'FINANCE_V2' }
      },

      // No flag — core module
      {
        path:         'crews',
        loadChildren: () => import('./views/crews/crews.module').then(m => m.CrewsModule),
        data:         { title: 'Crew Locator', breadcrumb: 'Crew Locator' }
      },

      // No flag — core module
      {
        path:         'fleet',
        loadChildren: () => import('./views/equipment/equipment.module').then(m => m.EquipmentModule),
        data:         { title: 'Fleet', breadcrumb: 'Fleet' }
      },

      // Example: a brand-new section that is flag-gated from day one
      // Uncomment and add to FEATURE_VARIANT_REGISTRY when ready
      // {
      //   path:          'analytics',
      //   canActivate:   [featureFlagGuard],
      //   loadChildren:  () => import('./views/analytics/analytics.module').then(m => m.AnalyticsModule),
      //   data: {
      //     title:       'Analytics',
      //     breadcrumb:  'Analytics',
      //     featureCode: 'ANALYTICS',
      //   }
      // },

    ]
  },

  {
    path:      '**',
    redirectTo: 'sessions/404'
  },
];

@NgModule({
  imports:  [RouterModule.forRoot(rootRouterConfig)],
  exports:  [RouterModule]
})
export class AppRoutingModule {}
